'use client';

import { useState } from 'react';
import { Medal } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FieldHelp } from '@/features/guided-help';
import type { Category } from '@/types/tournament.model';
import type { Sport } from '@/types';
import { CategoryRangeSlider } from './CategoryRangeSlider';

export interface CategoryRangeFieldProps {
  sport: Sport | null | undefined;
  onChange: (
    minCategory: Category,
    maxCategory: Category,
    forceSum: boolean,
  ) => void;
  initialMinCategory?: Category;
  initialMaxCategory?: Category;
  initialForceSum?: boolean;
  /**
   * Kept for API compatibility; both use the same card layout (header + content in one Card).
   * @deprecated No visual difference — prefer default.
   */
  layout?: 'plain' | 'shell';
}

export function CategoryRangeField({
  sport,
  onChange,
  initialMinCategory,
  initialMaxCategory,
  initialForceSum,
}: CategoryRangeFieldProps) {
  const hasSport = Boolean(sport?.uid);

  // The body switches between a range slider and a sum dropdown, so the card's
  // own title and copy have to follow it.
  const [forceSum, setForceSum] = useState(Boolean(initialForceSum));

  const handleChange = (
    minCategory: Category,
    maxCategory: Category,
    nextForceSum: boolean,
  ) => {
    setForceSum(nextForceSum);
    onChange(minCategory, maxCategory, nextForceSum);
  };

  if (!hasSport) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Medal className="h-5 w-5 text-foreground" />
            Rango de categorías
          </CardTitle>
          <CardDescription>
            Selecciona un deporte primero para ver las categorías disponibles.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card data-guide-id="wizard.team.categories">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Medal className="h-5 w-5 text-foreground" />
          {forceSum ? 'Suma de categorías' : 'Rango de categorías'}
          {/* The card swaps modes, so the inline help follows it. */}
          <FieldHelp
            topicId={
              forceSum ? 'wizard.team.forceSum' : 'wizard.team.categories'
            }
          />
        </CardTitle>
        <CardDescription>
          {forceSum
            ? 'Define cuánto tienen que sumar las categorías de la pareja.'
            : 'Define el rango de categorías permitidas para los participantes.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CategoryRangeSlider
          sport={sport!}
          onChange={handleChange}
          initialMinCategory={initialMinCategory}
          initialMaxCategory={initialMaxCategory}
          initialForceSum={initialForceSum}
          wrapInCard={false}
          hideInnerTitle
        />
      </CardContent>
    </Card>
  );
}
